import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaComment,
  FaCheck,
  FaPaperPlane,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaEnvelopeOpen,
  FaSpinner,
  FaExclamationCircle
} from 'react-icons/fa';
import { LuPiano } from "react-icons/lu";
import '../../assets/css/contato-orcamento.css';
import SeoHead from "../../components/Menu/components/SeoHead";

const partsOptions = [
  { id: 'geral', name: 'Reforma Geral', image: '/img/orcamento/piano_geral.jpg' },
  { id: 'movel', name: 'Móvel', image: '/img/orcamento/piano_movel.jpg' },
  { id: 'mecanismo', name: 'Mecanismo', image: '/img/orcamento/piano_mecanismo.jpg' },
  { id: 'encordamento', name: 'Encordamento', image: '/img/orcamento/piano_encordamento.jpg' }
];

const contatoOrcamentoSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness", 
  "name": "Waldir Pianos",
  "image": "https://pianoswaldir.com.br/images/logo.jpg", 
  "url": "https://pianoswaldir.com.br/contato-orcamento",
  "telephone": "+55-11-2256-5987",
  "email": "mailto:comercial@pianoswaldir.com.br",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rua Waldemar Martins, 695",
    "addressLocality": "São Paulo",
    "addressRegion": "SP",
    "postalCode": "02535-000",
    "addressCountry": "BR"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "08:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Saturday",
        "Sunday"
      ],
      "description": "Agendado" 
    }
  ],
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+55-11-2256-5987",
      "contactType": "customer service",
      "areaServed": "São Paulo"
    },
    {
      "@type": "ContactPoint",
      "telephone": "+55-11-96995-3700", 
      "contactType": "WhatsApp",
      "areaServed": "São Paulo"
    },
    {
      "@type": "ContactPoint",
      "email": "mailto:comercial@pianoswaldir.com.br",
      "contactType": "sales"
    },
    {
      "@type": "ContactPoint",
      "telephone": "+55-11-2256-5987",
      "contactType": "billing"
    }
    
  ],
  "makesOffer": {
    "@type": "Service",
    "serviceType": "Solicitação de Orçamento para Restauração de Pianos",
    "description": "Formulário para solicitar orçamento personalizado para reforma de pianos, incluindo serviços de móvel, mecanismo, encordoamento e reforma geral.",
    "provider": {
        "@type": "LocalBusiness",
        "name": "Waldir Pianos"
    }
  }
};

const ContatoOrcamento = () => {
  const [selectedParts, setSelectedParts] = useState([]);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [activeTab, setActiveTab] = useState('orcamento');
  
  // Estados para controlar o envio do formulário
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error' ou null

  const togglePartSelection = (part) => {
    setSelectedParts(prev => {
      if (prev.some(p => p.id === part.id)) {
        if (part.id === 'geral') {
          return prev.filter(p => p.id !== 'geral');
        }
        return prev.filter(p => p.id !== part.id);
      } else {
        if (part.id === 'geral') {
          return [part];
        }
        return [...prev.filter(p => p.id !== 'geral'), part];
      }
    });
    
    setActiveOverlay(part.id);
    setTimeout(() => setActiveOverlay(null), 1000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedParts.length === 0) {
      alert('Por favor, selecione pelo menos um serviço para o orçamento.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const payload = { ...formData, selectedParts };

    try {
      // IMPORTANTE: Confirme se esta é a URL correta do seu script no servidor
      const response = await fetch('https://pianoswaldir.com.br/api/enviar_orcamento.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        // Limpa o formulário após o sucesso
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSelectedParts([]);
      } else {
        console.error('Erro retornado pelo servidor:', result.message);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erro de rede ou de comunicação:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contato-orcamento-container">
      <SeoHead
        title="Contato Waldir Pianos | Orçamento para Restauração de Pianos em SP"
        description="Entre em contato com a Waldir Pianos para solicitar um orçamento personalizado para reforma e restauração do seu piano. Endereço, telefone, WhatsApp e formulário de contato disponíveis."
        keywords="contato Waldir Pianos, orçamento piano, reforma de piano, restauração piano São Paulo, telefone Waldir Pianos, WhatsApp piano, endereço Waldir Pianos"
        image="https://pianoswaldir.com.br/img/orcamento/piano.jpg" 
        url="https://pianoswaldir.com.br/contato-orcamento"
        canonicalUrl="https://pianoswaldir.com.br/contato-orcamento"
        schemaMarkup={contatoOrcamentoSchema}
      />
      
      <motion.header 
        className="contato-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="contato-header-content">
          <motion.h1 
            className="contato-main-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <LuPiano className="contato-title-icon" />
            Contato & Orçamento
            <span className="contato-title-decoration"></span>
          </motion.h1>
        </div>
      </motion.header>

      <div className="contato-tabs">
        <button 
          className={`contato-tab ${activeTab === 'orcamento' ? 'active' : ''}`}
          onClick={() => setActiveTab('orcamento')}
        >
          Orçamento
        </button>
        <button 
          className={`contato-tab ${activeTab === 'contato' ? 'active' : ''}`}
          onClick={() => setActiveTab('contato')}
        >
          Contato
        </button>
      </div>

      <div className="contato-main-content">
        {activeTab === 'orcamento' && (
          <div className="contato-orcamento-section">
            <motion.div 
              className="contato-piano-section"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className='contato-piano-coluna'>
                <div className="contato-piano-image-container">
                  <img 
                    src="/img/orcamento/piano.jpg" 
                    alt="Piano" 
                    className="contato-base-piano-image" 
                  />
                  
                  {partsOptions.map(part => (
                    <img
                      key={part.id}
                      src={part.image}
                      alt={`Parte ${part.name}`}
                      className={`contato-piano-overlay ${activeOverlay === part.id ? 'active' : ''}`}
                    />
                  ))}
                </div>

                <div className="contato-orcamento-header">
                  <h2 className="contato-orcamento-title">Orçamento Personalizado</h2>
                  <p className="contato-orcamento-subtitle">Selecione os serviços necessários para seu piano</p>
                </div>

                <div className="contato-part-selector">
                  {partsOptions.map(part => (
                    <div 
                      key={part.id}
                      className={`contato-part-option ${selectedParts.some(p => p.id === part.id) ? 'selected' : ''}`}
                      onClick={() => togglePartSelection(part)}
                    >
                      {part.name}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="contato-form-section"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {selectedParts.length > 0 && (
                <motion.div 
                  className="contato-selected-parts"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <h3 className="contato-selected-parts-title">
                    <FaCheck /> Serviços selecionados
                  </h3>
                  <div className="contato-selected-parts-list">
                    {selectedParts.map(part => (
                      <span key={part.id} className="contato-selected-part-tag">
                        {part.name}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
              
              <form className="contato-orcamento-form" onSubmit={handleSubmit}>
                <div className="contato-input-group">
                  <label htmlFor="name" className="contato-form-label">Nome</label>
                  <div className="contato-input-wrapper">
                    <FaUser className="contato-input-icon" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="contato-form-input"
                      placeholder="Seu nome completo"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="contato-input-group">
                  <label htmlFor="email" className="contato-form-label">E-mail</label>
                  <div className="contato-input-wrapper">
                    <FaEnvelope className="contato-input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="contato-form-input"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="contato-input-group">
                  <label htmlFor="phone" className="contato-form-label">WhatsApp/Celular</label>
                  <div className="contato-input-wrapper">
                    <FaPhone className="contato-input-icon" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="contato-form-input"
                      placeholder="(00) 00000-0000"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="contato-input-group">
                  <label htmlFor="message" className="contato-form-label">Mensagem</label>
                  <div className="contato-input-wrapper">
                    <FaComment className="contato-input-icon" />
                    <textarea
                      id="message"
                      name="message"
                      className="contato-form-input contato-form-textarea"
                      placeholder="Descreva o serviço necessário..."
                      value={formData.message}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                
                <motion.button
                  type="submit"
                  className="contato-submit-button"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <><FaSpinner className="spin-icon" /> Enviando...</>
                  ) : (
                    <><FaPaperPlane /> Solicitar Orçamento</>
                  )}
                </motion.button>

                {submitStatus === 'success' && (
                  <div className="feedback-message success">
                    <FaCheck /> Orçamento enviado com sucesso! Agradecemos o contato.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="feedback-message error">
                    <FaExclamationCircle /> Houve um erro ao enviar. Por favor, tente novamente ou entre em contato por outro canal.
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        )}

        {activeTab === 'contato' && (
          <div className="contato-info-section">
            <div className="contato-info-container">
              <div className="contato-info-card">
                <h3 className="contato-info-title">
                  <FaMapMarkerAlt className="contato-info-icon" />
                  Endereço
                </h3>
                <p className="contato-info-text">
                  Rua Waldemar Martins, 695<br />
                  Casa Verde - CEP: 02535-000<br />
                  São Paulo/SP
                </p>
              </div>

              <div className="contato-info-card">
                <h3 className="contato-info-title">
                  <FaClock className="contato-info-icon" />
                  Horário de Funcionamento
                </h3>
                <p className="contato-info-text">
                  Segunda a Sexta: 8h às 18h<br />
                  Sábado: Agendado<br />
                  Domingo: Agendado
                </p>
              </div>

              <div className="contato-info-card">
                <h3 className="contato-info-title">
                  <FaPhone className="contato-info-icon" />
                  Telefone
                </h3>
                <p className="contato-info-text">
                  11 2256-5987
                </p>
              </div>

              <div className="contato-info-card">
                <h3 className="contato-info-title">
                  <FaEnvelopeOpen className="contato-info-icon" />
                  E-mail
                </h3>
                <p className="contato-info-text">
                  comercial@pianoswaldir.com.br
                </p>
              </div>
            </div>

            <div className="contato-action-buttons">
              <motion.a
                href="https://wa.me/+5511969953700"
                className="contato-whatsapp-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                target="_blank" rel="noopener noreferrer"
              >
                <FaWhatsapp /> Conversar no WhatsApp
              </motion.a>

              <motion.a
                href="tel:1122565987"
                className="contato-phone-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPhone /> Ligar Agora
              </motion.a>
            </div>

            <div className="contato-map-container">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3659.0218580320293!2d-46.65537448878843!3d-23.495722178756633!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94cef7951165442d%3A0x6ffb284bc0d07fb1!2sR.%20Valdemar%20Martins%2C%20695%20-%20Parque%20Peruche%2C%20S%C3%A3o%20Paulo%20-%20SP%2C%2002535-000!5e0!3m2!1spt-BR!2sbr!4v1748504105985!5m2!1spt-BR!2sbr"
                className="contato-map"
                allowFullScreen=""
                loading="lazy"
                title="Localização Pianos Waldir"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContatoOrcamento;